import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  useTheme
} from '@mui/material';
import './ExerciseLibrary.css';
import './DialogOverrides.css'; // Import our button override styles

function ExerciseLibrary() {
  const theme = useTheme();
  const [exercises, setExercises] = useState([]);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMuscleGroup, setFilterMuscleGroup] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Load exercises from localStorage on mount
  useEffect(() => {
    try {
      // Load models created with the new ExerciseModelTrainer
      const trainedModels = JSON.parse(localStorage.getItem('trainedExerciseModels') || '[]');
      
      // Load previously trained exercises (legacy format)
      const trainedExercises = JSON.parse(localStorage.getItem('trainedExercises') || '[]');
      
      // Load recorded exercises (from old ExerciseRecorder)
      const storedExercises = Object.keys(localStorage)
        .filter(key => key.startsWith('exercise-'))
        .map(key => {
          const data = JSON.parse(localStorage.getItem(key));
          return {
            ...data,
            id: key
          };
        });
        
      // Format dates for all exercises
      const allExercises = [...trainedModels, ...trainedExercises, ...storedExercises]
        .map(exercise => ({
          ...exercise,
          dateFormatted: exercise.dateCreated ? new Date(exercise.dateCreated).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          }) : 'Unknown'
        }));
        
      setExercises(allExercises);
    } catch (error) {
      console.error('Error loading exercises:', error);
      setExercises([]);
    }
  }, []);

  const handleViewDetails = (exercise) => {
    setSelectedExercise(exercise);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setConfirmDelete(false);
  };

  // Delete an exercise from localStorage
  const handleDeleteExercise = (exercise) => {
    if (confirmDelete) {
      try {
        // Remove from localStorage based on storage type
        if (exercise.id && exercise.id.startsWith('exercise-')) {
          localStorage.removeItem(exercise.id);
        } else if (exercise.id && exercise.id.startsWith('model-')) {
          // Handle model deletion
          const trainedModels = JSON.parse(localStorage.getItem('trainedExerciseModels') || '[]');
          const updatedModels = trainedModels.filter(model => model.id !== exercise.id);
          localStorage.setItem('trainedExerciseModels', JSON.stringify(updatedModels));
        } else {
          // Handle legacy trained exercise deletion
          const trainedExercises = JSON.parse(localStorage.getItem('trainedExercises') || '[]');
          const updatedExercises = trainedExercises.filter(ex => 
            ex.name !== exercise.name || ex.dateCreated !== exercise.dateCreated
          );
          localStorage.setItem('trainedExercises', JSON.stringify(updatedExercises));
        }
        
        // Update the exercises state
        setExercises(prev => prev.filter(ex => ex !== exercise));
        handleCloseDialog();
        
      } catch (error) {
        console.error('Error deleting exercise:', error);
      }
    } else {
      setConfirmDelete(true);
    }
  };

  // Function to test an exercise
  const handleTestExercise = (exercise) => {
    // In a real implementation, this would launch the exercise in a test mode
    console.log(`Testing exercise: ${exercise.name}`);
    alert(`Exercise "${exercise.name}" would now launch in test mode. This feature is coming soon.`);
  };

  // Filter exercises based on search query and muscle group filter
  const filteredExercises = exercises.filter(exercise => {
    const matchesSearch = exercise.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                         (exercise.description && exercise.description.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesMuscleGroup = !filterMuscleGroup || 
                              (exercise.muscleGroup && exercise.muscleGroup === filterMuscleGroup);
    return matchesSearch && matchesMuscleGroup;
  });

  // Extract all unique muscle groups from the exercises
  const muscleGroups = [...new Set(exercises.map(exercise => exercise.muscleGroup))].filter(Boolean);

  return (
    <Box className="exercise-library">
      <Typography variant="h5" gutterBottom>
        Exercise Library
      </Typography>
      
      <Box sx={{ mb: 4, display: 'flex', flexDirection: {xs: 'column', sm: 'row'}, gap: 2 }}>
        <TextField
          label="Search Exercises"
          variant="outlined"
          fullWidth
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ flex: 2 }}
        />
        
        <FormControl variant="outlined" sx={{ minWidth: 200, flex: 1 }}>
          <InputLabel id="muscle-filter-label">Filter by Muscle</InputLabel>
          <Select
            labelId="muscle-filter-label"
            value={filterMuscleGroup}
            onChange={(e) => setFilterMuscleGroup(e.target.value)}
            label="Filter by Muscle"
          >
            <MenuItem value="">
              <em>All Muscle Groups</em>
            </MenuItem>
            {muscleGroups.map((group) => (
              <MenuItem key={group} value={group}>{group}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      
      {filteredExercises.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No exercises found. Create new exercises using the Record or Create tabs.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredExercises.map((exercise) => (
            <Grid item xs={12} sm={6} md={4} key={exercise.id}>
              <Card className="exercise-card">
                <CardContent>
                  <Typography variant="h6" component="div" className="exercise-card-title">
                    {exercise.name}
                  </Typography>
                  
                  {exercise.muscleGroup && (
                    <Chip 
                      label={exercise.muscleGroup} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                      className="muscle-chip"
                    />
                  )}
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {exercise.description || 'No description available'}
                  </Typography>
                  
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      Created: {exercise.dateFormatted}
                    </Typography>
                    
                    {exercise.repetitionCount && (
                      <Chip 
                        label={`${exercise.repetitionCount} reps`} 
                        size="small" 
                        color="secondary"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </CardContent>
                <CardActions>
                  <Button 
                    size="small" 
                    color="primary"
                    onClick={() => handleViewDetails(exercise)}
                  >
                    View Details
                  </Button>
                  <Button 
                    size="small" 
                    color="secondary"
                  >
                    Use in Workout
                  </Button>
                  <IconButton 
                    size="small" 
                    color="error"
                    onClick={() => {
                      setSelectedExercise(exercise);
                      setConfirmDelete(true);
                      setOpenDialog(true);
                    }}
                    aria-label="delete"
                  >
                    <Box component="span" sx={{ fontSize: '1.25rem' }}>🗑️</Box>
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      
      {/* Exercise Detail Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: '#1e1e1e'
          }
        }}
      >
        {selectedExercise && (
          <>
            <DialogTitle>{selectedExercise.name}</DialogTitle>
            <DialogContent className="exercise-dialog-content" dividers>
              <Box sx={{ mb: 2 }}>
                {selectedExercise.muscleGroup && (
                  <Chip 
                    label={selectedExercise.muscleGroup} 
                    color="primary" 
                    variant="outlined"
                    sx={{ mr: 1 }}
                  />
                )}
                {selectedExercise.detectionFocus && (
                  <Chip 
                    label={`Focus: ${selectedExercise.detectionFocus}`}
                    color="secondary"
                    variant="outlined"
                  />
                )}
              </Box>
              
              <Typography variant="subtitle1" gutterBottom>
                Description
              </Typography>
              <Typography variant="body2" paragraph>
                {selectedExercise.description || 'No description available'}
              </Typography>
              
              <Typography variant="subtitle1" gutterBottom>
                Training Details
              </Typography>
              <Box sx={{ mb: 2 }}>
                {selectedExercise.repetitionCount && (
                  <Typography variant="body2">
                    <strong>Sample repetitions:</strong> {selectedExercise.repetitionCount}
                  </Typography>
                )}
                {selectedExercise.sampleCount && (
                  <Typography variant="body2">
                    <strong>Training frames:</strong> {selectedExercise.sampleCount}
                  </Typography>
                )}
                <Typography variant="body2">
                  <strong>Created:</strong> {selectedExercise.dateFormatted}
                </Typography>
              </Box>
              
              <Typography variant="subtitle1" gutterBottom>
                Usage Instructions
              </Typography>
              <Typography variant="body2">
                This exercise can be used with the exercise tracking feature. Select it from the
                dropdown menu in the exercise tracker to start counting repetitions.
              </Typography>
            </DialogContent>
            <DialogActions 
              className="exercise-dialog-actions"
              sx={{ p: 2 }}
            >
              {confirmDelete ? (
                <>
                  <Typography variant="body2" color="error" sx={{ flex: 1 }}>
                    Are you sure you want to delete this exercise?
                  </Typography>
                  <Button 
                    onClick={() => setConfirmDelete(false)}
                    sx={{ 
                      bgcolor: 'transparent',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.08)'
                      }
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    color="error" 
                    variant="contained"
                    onClick={() => handleDeleteExercise(selectedExercise)}
                    sx={{ 
                      bgcolor: theme.palette.error.main,
                      '&:hover': {
                        bgcolor: theme.palette.error.dark
                      }
                    }}
                  >
                    Delete
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    onClick={handleCloseDialog}
                    sx={{ 
                      bgcolor: 'transparent',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.08)'
                      }
                    }}
                  >
                    Close
                  </Button>
                  <Button 
                    onClick={() => handleTestExercise(selectedExercise)} 
                    color="secondary" 
                    variant="outlined"
                    sx={{ 
                      bgcolor: 'transparent',
                      borderColor: theme.palette.secondary.main,
                      color: theme.palette.secondary.main,
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.08)'
                      }
                    }}
                  >
                    Test Exercise
                  </Button>
                  <Button 
                    color="primary" 
                    variant="contained"
                    sx={{ 
                      bgcolor: theme.palette.primary.main,
                      '&:hover': {
                        bgcolor: theme.palette.primary.dark
                      }
                    }}
                  >
                    Use This Exercise
                  </Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}

export default ExerciseLibrary;
